import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings, Briefcase, Images, MessageSquare, Users, Mail, FolderKanban,
  ChevronRight, BookOpen, LayoutDashboard, ArrowRight, Lightbulb
} from 'lucide-react';

const sections = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/admin/dashboard',
    accent: '#4A5D23',
    video: 'https://customer-assets.emergentagent.com/job_1e51cca1-1ea3-4d7b-99bb-feec889ac148/artifacts/63iw9iu4_Dashboard.webm',
    overview: 'Your home base. See your content stats and jump to common tasks.',
    steps: [
      'Check your services, portfolio, testimonials, and inquiry counts at a glance.',
      'Use Quick Actions to jump straight to managing services, portfolio, or inquiries.',
      'Click "View Site" in the top bar to preview your public website anytime.',
    ],
  },
  {
    id: 'site-content',
    title: 'Site Content',
    icon: Settings,
    path: '/admin/site-content',
    accent: '#5B6E3E',
    video: 'https://customer-assets.emergentagent.com/job_1e51cca1-1ea3-4d7b-99bb-feec889ac148/artifacts/do9o7uj3_Site%20Content.webm',
    overview: 'Edit the text and images on your public website pages.',
    steps: [
      'Select a page section using the tabs at the top (Hero, About, Contact, etc.).',
      'Update any text field — taglines, subtitles, descriptions.',
      'Upload new images by clicking the upload area or dragging a file.',
      'Click "Save Changes" to publish. Changes appear on your site immediately.',
    ],
  },
  {
    id: 'services',
    title: 'Services',
    icon: Briefcase,
    path: '/admin/services',
    accent: '#6B7F3A',
    video: 'https://customer-assets.emergentagent.com/job_1e51cca1-1ea3-4d7b-99bb-feec889ac148/artifacts/2lqr2g5t_Services.webm',
    overview: 'Manage the wedding and event services you offer.',
    steps: [
      'Click "+ Add Service" to create a new service.',
      'Add a title, detailed description, and a high-quality image (800x600px minimum).',
      'List key features so clients know exactly what\'s included.',
      'Use the pencil icon to edit or the trash icon to delete any service.',
    ],
    tip: 'Prices are inquiry-only — clients will contact you for quotes.',
  },
  {
    id: 'portfolio',
    title: 'Portfolio',
    icon: Images,
    path: '/admin/portfolio',
    accent: '#D4AF37',
    video: 'https://customer-assets.emergentagent.com/job_1e51cca1-1ea3-4d7b-99bb-feec889ac148/artifacts/fdsmge92_Portfolio.webm',
    overview: 'Showcase your best work with event photos.',
    steps: [
      'Click "+ Add Item" and upload your best event photos.',
      'Set a category (Weddings, Corporate, Bridal Showers, etc.) for each item.',
      'Give each entry a descriptive title like "Johnson Wedding at Rose Garden".',
      'Edit or delete items anytime using the icons on each card.',
    ],
    tip: 'Keep this updated after every event. Aim for 10–15 items across categories.',
  },
  {
    id: 'testimonials',
    title: 'Testimonials',
    icon: MessageSquare,
    path: '/admin/testimonials',
    accent: '#C06C55',
    video: 'https://customer-assets.emergentagent.com/job_1e51cca1-1ea3-4d7b-99bb-feec889ac148/artifacts/gnr84kti_Testimonials.webm',
    overview: 'Display reviews and quotes from happy clients.',
    steps: [
      'Click "+ Add Testimonial" and enter the client\'s name and event type.',
      'Add their quote — keep it genuine and highlight what they loved.',
      'Set a star rating (1–5) and optionally upload their photo.',
      'Edit or remove testimonials with the pencil and trash icons.',
    ],
    tip: 'Ask clients for a quote right after their event when the experience is fresh!',
  },
  {
    id: 'vendors',
    title: 'Preferred Vendors',
    icon: Users,
    path: '/admin/vendors',
    accent: '#8B7355',
    video: 'https://customer-assets.emergentagent.com/job_1e51cca1-1ea3-4d7b-99bb-feec889ac148/artifacts/qc0etay5_Vendors.webm',
    overview: 'Maintain your list of recommended vendors.',
    steps: [
      'Click "+ Add Vendor" to add a florist, caterer, photographer, DJ, etc.',
      'Include their name, category, description, and contact information.',
      'Upload their logo or a photo for the listing.',
      'Keep contact info current — edit anytime with the pencil icon.',
    ],
  },
  {
    id: 'inquiries',
    title: 'Inquiries',
    icon: Mail,
    path: '/admin/inquiries',
    accent: '#5B6E3E',
    video: 'https://customer-assets.emergentagent.com/job_1e51cca1-1ea3-4d7b-99bb-feec889ac148/artifacts/ephaho2k_Inquiries.webm',
    overview: 'View and manage contact form submissions.',
    steps: [
      'New inquiries appear here with the client\'s name, email, event type, and date.',
      'Click the eye icon to see full details: budget, guest count, venue, and message.',
      'Update the status (New → Contacted → Booked → Closed) as you follow up.',
      'An email notification is also sent to help@edweddingeventsandcreations.com for each new inquiry.',
    ],
    tip: 'Respond quickly — fast replies dramatically increase your booking rate!',
  },
  {
    id: 'clients',
    title: 'Clients',
    icon: Users,
    path: '/admin/clients',
    accent: '#4A5D23',
    video: 'https://customer-assets.emergentagent.com/job_1e51cca1-1ea3-4d7b-99bb-feec889ac148/artifacts/xgulgnwl_Clients.webm',
    overview: 'Create portal accounts so clients can track their event progress.',
    steps: [
      'Click "+ Add Client" and enter their name, email, password, event type, and date.',
      'A welcome email with login credentials is sent to the client automatically.',
      'Use the "Notify" button to send custom email updates to any client.',
      'Edit client details with the pencil icon, or remove with the trash icon.',
    ],
    tip: 'Clients log in at /portal/login with the email and password you set.',
  },
  {
    id: 'projects',
    title: 'Project Guides',
    icon: FolderKanban,
    path: '/admin/projects',
    accent: '#D4AF37',
    video: 'https://customer-assets.emergentagent.com/job_1e51cca1-1ea3-4d7b-99bb-feec889ac148/artifacts/qi2nfsdb_Projects.webm',
    overview: 'Build visual timelines so clients can track their event milestones.',
    steps: [
      'Select a client from the cards to open their project editor.',
      'Click "Add Milestone" — set a title, description, target date, and status.',
      'Update milestone statuses as you progress: Pending → In Progress → Completed.',
      'Click "Save & Notify Client" to save changes and email the client an update.',
    ],
    tip: 'Common milestones: Venue Confirmed, Vendor Meetings, Menu Tasting, Final Walkthrough, Event Day.',
  },
];

const AdminTutorial = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const current = sections.find(s => s.id === activeSection);

  return (
    <div data-testid="admin-tutorial">
      <div className="mb-8">
        <h1 className="font-heading text-3xl text-[#2C2C2C] flex items-center gap-3">
          <BookOpen size={28} className="text-[#4A5D23]" />
          Admin Guide
        </h1>
        <p className="text-[#6B7280] mt-1">Everything you need to know to manage your website.</p>
      </div>

      <div className="flex gap-6">
        {/* Left: Section List */}
        <div className="w-64 flex-shrink-0 space-y-1">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              data-testid={`tutorial-nav-${s.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                activeSection === s.id
                  ? 'bg-[#4A5D23] text-white shadow-md'
                  : 'text-[#2C2C2C] hover:bg-[#F5F5F0]'
              }`}
            >
              <s.icon size={18} className={activeSection === s.id ? 'text-[#D4AF37]' : 'text-[#6B7280]'} />
              <span className="text-sm font-medium">{s.title}</span>
            </button>
          ))}
        </div>

        {/* Right: Active Section Content */}
        {current && (
          <div className="flex-1 min-w-0" data-testid={`tutorial-content-${current.id}`}>
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-soft p-6 mb-4">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: current.accent }}>
                  <current.icon className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="font-heading text-2xl text-[#2C2C2C]">{current.title}</h2>
                  <p className="text-[#6B7280] text-sm">{current.overview}</p>
                </div>
              </div>
              <Link
                to={current.path}
                className="inline-flex items-center gap-2 text-sm font-medium text-[#4A5D23] hover:text-[#D4AF37] transition-colors mt-1"
              >
                Open {current.title} <ArrowRight size={16} />
              </Link>
            </div>

            {/* Video Tutorial */}
            {current.video && (
              <div className="bg-white rounded-2xl shadow-soft p-6 mb-4">
                <h3 className="text-sm font-bold text-[#2C2C2C] uppercase tracking-wider mb-4">Video Walkthrough</h3>
                <video
                  key={current.id}
                  controls
                  className="w-full rounded-xl"
                  preload="metadata"
                >
                  <source src={current.video} type="video/webm" />
                  Your browser does not support the video tag.
                </video>
              </div>
            )}

            {/* Steps */}
            <div className="bg-white rounded-2xl shadow-soft p-6 mb-4">
              <h3 className="text-sm font-bold text-[#2C2C2C] uppercase tracking-wider mb-4">How to Use</h3>
              <div className="space-y-4">
                {current.steps.map((step, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
                      style={{ backgroundColor: current.accent }}
                    >
                      {i + 1}
                    </div>
                    <p className="text-[#2C2C2C] text-sm leading-relaxed pt-1">{step}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tip */}
            {current.tip && (
              <div className="bg-[#FFFDF5] border border-[#D4AF37]/30 rounded-2xl p-5 flex gap-4">
                <Lightbulb size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-[#2C2C2C] mb-1">Pro Tip</p>
                  <p className="text-sm text-[#6B7280] leading-relaxed">{current.tip}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-8 text-center">
        <p className="text-[#6B7280] text-sm">
          Need more help? Contact{' '}
          <a href="mailto:help@edweddingeventsandcreations.com" className="text-[#4A5D23] font-medium hover:underline">
            help@edweddingeventsandcreations.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default AdminTutorial;
